// Transactional operations wrapper for idb-keyval
// Provides pseudo-transactional behavior without Dexie
// This is a workaround until Dexie can be installed

import { db, cacheStore, imageStore, metadataStore, syncQueueStore } from './database.js';

class TransactionalOperation {
  constructor() {
    this.operations = [];
    this.completed = false;
  }

  /**
   * Add operation to transaction
   */
  add(store, key, value, operation = 'set') {
    this.operations.push({ store, key, value, operation });
    return this;
  }

  /**
   * Execute all operations atomically (best effort)
   * Note: This is not true ACID, but provides compensation on failure
   */
  async execute() {
    if (this.completed) {
      throw new Error('Transaction already executed');
    }

    const results = [];
    const executedOperations = [];

    try {
      // Execute operations in order
      for (const op of this.operations) {
        let result;
        
        switch (op.operation) {
          case 'set':
            result = await db.set(op.key, op.value, op.store);
            break;
          case 'delete':
            result = await db.delete(op.key, op.store);
            break;
          case 'get':
            result = await db.get(op.key, op.store);
            break;
          default:
            throw new Error(`Unknown operation: ${op.operation}`);
        }

        results.push(result);
        executedOperations.push(op);
      }

      this.completed = true;
      return results;
    } catch (error) {
      console.error('[Transactional] Operation failed, attempting rollback:', error);
      
      // Attempt rollback of executed operations
      await this.rollback(executedOperations);
      
      throw new Error(`Transaction failed and rolled back: ${error.message}`);
    }
  }

  /**
   * Rollback executed operations (best effort)
   */
  async rollback(executedOperations) {
    const rollbackErrors = [];

    // Reverse operations for rollback
    for (let i = executedOperations.length - 1; i >= 0; i--) {
      const op = executedOperations[i];

      try {
        switch (op.operation) {
          case 'set':
            // Rollback by deleting
            await db.delete(op.key, op.store);
            break;
          case 'delete':
            // Rollback by restoring (if we had the old value)
            // This is a limitation - we can't restore deleted values
            console.warn('[Transactional] Cannot rollback delete operation:', op.key);
            break;
          case 'get':
            // Reads don't need rollback
            break;
        }
      } catch (error) {
        rollbackErrors.push(error);
        console.error('[Transactional] Rollback failed:', error);
      }
    }

    if (rollbackErrors.length > 0) {
      console.error('[Transactional] Partial rollback occurred:', rollbackErrors);
    }
  }
}

/**
 * Create a transaction for entity update with queue insertion
 * This is the critical operation that needs atomicity
 */
export async function transactionalEntityUpdate(entityId, entityType, updates, mutation) {
  const transaction = new TransactionalOperation();

  // 1. Update entity
  const entityKey = `${entityType}:${entityId}`;
  transaction.add(cacheStore, entityKey, updates, 'set');

  // 2. Add to sync queue
  const mutationId = `mutation:${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  transaction.add(syncQueueStore, mutationId, mutation, 'set');

  // 3. Update metadata
  const metadataKey = `metadata:${entityKey}`;
  const metadataUpdate = {
    entityType,
    entityId,
    updatedAt: Date.now(),
    syncStatus: 'dirty',
  };
  transaction.add(metadataStore, metadataKey, metadataUpdate, 'set');

  try {
    await transaction.execute();
    return { success: true, mutationId };
  } catch (error) {
    console.error('[Transactional] Entity update transaction failed:', error);
    return { success: false, error };
  }
}

/**
 * Create a transaction for cache invalidation cascade
 */
export async function transactionalCacheInvalidation(cacheKeys, entityType) {
  const transaction = new TransactionalOperation();

  // Invalidate all related cache keys
  for (const key of cacheKeys) {
    transaction.add(cacheStore, key, null, 'delete');
  }

  // Update metadata to track invalidation
  const metadataKey = `invalidation:${entityType}:${Date.now()}`;
  const metadata = {
    entityType,
    invalidatedKeys: cacheKeys,
    timestamp: Date.now(),
  };
  transaction.add(metadataStore, metadataKey, metadata, 'set');

  try {
    await transaction.execute();
    return { success: true };
  } catch (error) {
    console.error('[Transactional] Cache invalidation transaction failed:', error);
    return { success: false, error };
  }
}

/**
 * Create a transaction for image storage with metadata
 */
export async function transactionalImageStore(url, blob, metadata) {
  const transaction = new TransactionalOperation();

  // Store image blob
  const imageKey = `image:${url}`;
  transaction.add(imageStore, imageKey, { url, blob, metadata }, 'set');

  // Update image metadata
  const metadataKey = `image-metadata:${url}`;
  transaction.add(metadataStore, metadataKey, metadata, 'set');

  try {
    await transaction.execute();
    return { success: true };
  } catch (error) {
    console.error('[Transactional] Image store transaction failed:', error);
    return { success: false, error };
  }
}

/**
 * Validate data integrity after operations
 */
export async function validateIntegrity(entityId, entityType) {
  const issues = [];

  try {
    // Check entity exists
    const entityKey = `${entityType}:${entityId}`;
    const entity = await db.get(entityKey, cacheStore);
    if (!entity) {
      issues.push('entity_missing');
    }

    // Check metadata exists
    const metadataKey = `metadata:${entityKey}`;
    const metadata = await db.get(metadataKey, metadataStore);
    if (!metadata) {
      issues.push('metadata_missing');
    }

    // Check sync status consistency
    if (entity && metadata) {
      if (entity.metadata?.syncStatus !== metadata.syncStatus) {
        issues.push('sync_status_mismatch');
      }
    }

    return { valid: issues.length === 0, issues };
  } catch (error) {
    console.error('[Transactional] Integrity validation failed:', error);
    return { valid: false, issues: ['validation_error'] };
  }
}

/**
 * Recover from corrupted state
 */
export async function recoverCorruptedState(entityId, entityType) {
  console.log('[Transactional] Attempting recovery for:', entityType, entityId);

  try {
    const validation = await validateIntegrity(entityId, entityType);
    
    if (validation.valid) {
      return { success: true, message: 'No corruption detected' };
    }

    // Recovery strategies based on issues
    if (validation.issues.includes('entity_missing') && validation.issues.includes('metadata_missing')) {
      // Both missing - clean state
      return { success: true, message: 'Clean state (both missing)' };
    }

    if (validation.issues.includes('entity_missing')) {
      // Entity missing but metadata exists - delete orphaned metadata
      const metadataKey = `metadata:${entityType}:${entityId}`;
      await db.delete(metadataKey, metadataStore);
      return { success: true, message: 'Removed orphaned metadata' };
    }

    if (validation.issues.includes('metadata_missing')) {
      // Metadata missing but entity exists - recreate metadata
      const entityKey = `${entityType}:${entityId}`;
      const entity = await db.get(entityKey, cacheStore);
      
      if (entity) {
        const metadataKey = `metadata:${entityKey}`;
        const metadata = {
          entityType,
          entityId,
          updatedAt: entity.metadata?.updatedAt || Date.now(),
          syncStatus: 'unknown',
        };
        await db.set(metadataKey, metadata, metadataStore);
        return { success: true, message: 'Recreated metadata' };
      }
    }

    if (validation.issues.includes('sync_status_mismatch')) {
      // Sync status mismatch - use entity's status as source of truth
      const entityKey = `${entityType}:${entityId}`;
      const entity = await db.get(entityKey, cacheStore);
      
      if (entity?.metadata?.syncStatus) {
        const metadataKey = `metadata:${entityKey}`;
        const metadata = await db.get(metadataKey, metadataStore);
        
        if (metadata) {
          metadata.syncStatus = entity.metadata.syncStatus;
          await db.set(metadataKey, metadata, metadataStore);
          return { success: true, message: 'Sync status reconciled' };
        }
      }
    }

    return { success: false, message: 'Could not recover', issues: validation.issues };
  } catch (error) {
    console.error('[Transactional] Recovery failed:', error);
    return { success: false, message: 'Recovery error', error };
  }
}
